import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { ARMLogo } from "@/components/ARMLogo";
import { ArrowRight, Languages, Sparkles, BadgeCheck, Quote } from "lucide-react";

/**
 * Public, unauthenticated landing showcasing verified consultants who have
 * opted into knowledge sharing (share_knowledge=true). Doubles as a signup
 * funnel — every card ends with a "Sign in / Join" CTA.
 */
export default function Featured() {
  const { t, lang, toggle } = useLang();
  const [state, setState] = useState({ loading: true, sharers: [], error: "" });

  useEffect(() => {
    let alive = true;
    api.get("/public/featured-sharers")
      .then((r) => { if (alive) setState({ loading: false, sharers: r.data.sharers || [], error: "" }); })
      .catch(() => { if (alive) setState({ loading: false, sharers: [], error: "featured_load_failed" }); });
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen bg-black text-[#F3F4F6]" data-testid="featured-page">
      {/* Top bar */}
      <header className="border-b border-[#262626] px-6 md:px-14 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="featured-brand">
          <ARMLogo size={36} />
          <span className="font-display text-base leading-tight max-w-[240px]">{t("brand")}</span>
        </Link>
        <div className="flex items-center gap-6">
          <button
            data-testid="featured-language-toggle"
            onClick={toggle}
            className="hidden sm:flex items-center gap-2 text-xs text-[#9CA3AF] hover:text-[#F5A623] uppercase tracking-[0.2em]"
          >
            <Languages size={14} />
            {lang === "en" ? "العربية" : "English"}
          </button>
          <Link
            to="/login"
            data-testid="featured-signin-link"
            className="text-xs text-[#9CA3AF] hover:text-[#F5A623] uppercase tracking-[0.2em]"
          >
            {t("featured_signin")}
          </Link>
          <Link
            to="/register"
            data-testid="featured-join-btn"
            className="bg-[#F5A623] text-black font-semibold rounded-sm px-4 py-2 text-sm hover:bg-[#D97706] transition-colors flex items-center gap-2"
          >
            {t("featured_join")} <ArrowRight size={14} />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 md:px-14 pt-14 pb-8 max-w-5xl">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#F5A623] mb-4">
          <Sparkles size={12} /> {t("featured_kicker")}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-4">
          {t("featured_title")}
        </h1>
        <p className="text-base text-[#9CA3AF] max-w-2xl">{t("featured_sub")}</p>
      </section>

      {/* Grid */}
      <section className="px-6 md:px-14 pb-24">
        {state.loading && (
          <div className="text-sm text-[#9CA3AF]" data-testid="featured-loading">{t("loading")}</div>
        )}
        {!state.loading && state.error && (
          <div
            data-testid="featured-error"
            className="border border-[#DC2626]/40 bg-[#DC2626]/5 rounded-sm px-6 py-8 text-center text-sm text-[#DC2626]"
          >
            {t("featured_load_failed")}
          </div>
        )}
        {!state.loading && !state.error && state.sharers.length === 0 && (
          <div
            data-testid="featured-empty"
            className="border border-dashed border-[#262626] rounded-sm px-6 py-16 text-center text-[#9CA3AF]"
          >
            {t("featured_none_yet")}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {state.sharers.map((s) => (
            <article
              key={s.alias}
              data-testid={`featured-card-${s.alias}`}
              className="bg-[#0A0A0A] border border-[#262626] hover:border-[#F5A623]/40 transition-colors rounded-sm p-6 flex flex-col"
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 border border-[#F5A623]/30 bg-[#F5A623]/5 flex items-center justify-center font-semibold text-[#F5A623]">
                  {s.alias.slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-semibold text-[#F3F4F6] truncate">{s.alias}</span>
                    <BadgeCheck size={14} className="text-[#22C55E] shrink-0" />
                  </div>
                  <div className="text-xs text-[#9CA3AF] truncate">{s.primary_industry}</div>
                </div>
              </div>

              {s.bio && (
                <p className="text-sm text-[#D1D5DB] leading-relaxed mb-4 line-clamp-4">
                  {s.bio}
                </p>
              )}

              <div className="space-y-2 mb-4 text-xs">
                <div>
                  <span className="text-[#6B7280] uppercase tracking-[0.15em] mr-2">{t("featured_areas")}:</span>
                  <span className="text-[#D1D5DB]">{(s.areas_of_interest || []).join(" · ")}</span>
                </div>
                <div>
                  <span className="text-[#6B7280] uppercase tracking-[0.15em] mr-2">{t("featured_countries")}:</span>
                  <span className="text-[#D1D5DB]">{(s.countries || []).join(", ")}</span>
                </div>
              </div>

              {s.endorsements && s.endorsements.length > 0 && (
                <div className="border-t border-[#262626] pt-4 mt-auto space-y-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#F5A623] flex items-center gap-1.5">
                    <Quote size={11} />
                    {t("featured_endorsements")} · {s.endorsement_count}
                  </div>
                  {s.endorsements.slice(0, 2).map((e, i) => (
                    <blockquote
                      key={i}
                      data-testid={`featured-endorsement-${s.alias}-${i}`}
                      className="text-xs text-[#D1D5DB] italic border-l-2 border-[#F5A623]/40 pl-3"
                    >
                      "{e.comment}"
                      <div className="not-italic text-[10px] text-[#6B7280] mt-1 uppercase tracking-[0.15em]">
                        — {t("featured_endorsed_by")} {e.by}
                      </div>
                    </blockquote>
                  ))}
                </div>
              )}

              <Link
                to="/register"
                data-testid={`featured-card-cta-${s.alias}`}
                className="mt-5 text-xs text-[#F5A623] hover:underline uppercase tracking-[0.2em] flex items-center gap-1"
              >
                {t("featured_view_full")} <ArrowRight size={12} />
              </Link>
            </article>
          ))}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="border-t border-[#262626] px-6 md:px-14 py-14">
        <div className="max-w-3xl">
          <h2 className="font-display text-2xl md:text-3xl mb-3">{t("featured_join")}</h2>
          <p className="text-sm text-[#9CA3AF] mb-6 max-w-xl">{t("welcome_back_sub")}</p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/register"
              data-testid="featured-footer-join-btn"
              className="bg-[#F5A623] text-black font-semibold rounded-sm px-6 py-3 hover:bg-[#D97706] flex items-center gap-2"
            >
              {t("featured_join")} <ArrowRight size={16} />
            </Link>
            <Link
              to="/login"
              data-testid="featured-footer-signin-link"
              className="border border-[#262626] hover:border-[#F5A623] rounded-sm px-6 py-3 text-sm"
            >
              {t("featured_signin")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
